import { Spinner } from '~/shadcn/ui/spinner'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '~/shadcn/ui/dialog'
import Analytics from '~/@lib_integrations/analytics/analytics'
import { AgreementPreview } from '~/@mid_ui/agreement-preview/AgreementPreview'
import { Text } from '~/@mid_ui/typography/Text'
import { ArrowForwardIcon } from '~/@mid_ui/icons/ArrowForwardIcon'
import { TwoFaVerifyForm } from '~/@mid_2fa/twofa-verify-form/TwoFaVerifyForm'
import type { UserWithMeta } from '~/@biz_account/useUser/types'
import type { AgreementPreviewRequest } from '~/@pages_investing/order/_order_types/order.types'
import { useOrderAgreement } from './useOrderAgreement'
import { FormError } from '~/@mid_ui-form/form-error/FormError'
import { useTwoFaCodeExpirationCountdown } from '~/@mid_2fa/twofa/useTwoFaCodeExpirationCountdown'
import { Button } from '~/shadcn/ui/button/button'
import { CodeExpirationCountdown } from '~/@mid_2fa/twofa/CodeExpirationCountdown'

type Props = {
  user: UserWithMeta
  orderParams: AgreementPreviewRequest
  // TODO: WOF campaign
  // specialBonus?: SpecialBonus;
  onClose: () => void
  onSuccess: (payload: AgreementPreviewRequest) => void
}

export function OrderAgreementDialog({
  user,
  orderParams,
  // specialBonus,
  onClose,
  onSuccess,
}: Props) {
  const {
    step,
    setStep,
    isAgreementPreviewLoading,
    agreement,
    agreementPreviewError,

    orderCreateVerifyMutation,
    isOrderCreateVerifyLoading,
    orderCreateVerifyCodeExpiresAt,
    orderCreateVerifyError,

    orderCreateSignMutation,
    isOrderCreateSignLoading,
    orderCreateSignError,
  } = useOrderAgreement({
    ...orderParams,
    // specialBonus,
  } satisfies AgreementPreviewRequest)

  const { countdown, hasCodeExpired } = useTwoFaCodeExpirationCountdown(
    orderCreateVerifyCodeExpiresAt
  )

  const resendCode = () => {
    Analytics.track('new-investment-step2-agreement-signed')
    orderCreateVerifyMutation(undefined, {
      onSuccess: () => {
        setStep('code')
      },
    })
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent
        className="max-w-[min(42rem,calc(100%-2rem))] p-6 md:p-12 text-sm"
        aria-describedby={undefined}>
        {!isAgreementPreviewLoading ? (
          <DialogTitle className="flex flex-col items-start text-start text-2xl mb-2">
            {agreement?.title || 'Podpis Dohody o pristúpení k úverovej zmluve'}
          </DialogTitle>
        ) : (
          <DialogTitle />
        )}

        {agreement == null && (
          <>
            {isAgreementPreviewLoading && (
              <div className="flex justify-center">
                <Spinner className="size-6" />
              </div>
            )}
            {!isAgreementPreviewLoading && agreementPreviewError && (
              <FormError error={agreementPreviewError} />
            )}
          </>
        )}

        {agreement && (
          <>
            {step === 'agreement' && (
              <>
                <div className="flex flex-col overflow-auto">
                  <div className="max-h-[40svh] overflow-overlay">
                    <AgreementPreview
                      agreementSk={agreement}
                      agreementLang={undefined}
                    />
                  </div>
                </div>
                {orderCreateVerifyError && (
                  <FormError error={orderCreateVerifyError} />
                )}
                <DialogFooter>
                  <div className="flex flex-col-reverse w-full justify-center lg:flex-row gap-4">
                    <Button
                      variant="outline"
                      size="2xl"
                      className="w-full lg:w-fit"
                      disabled={isOrderCreateVerifyLoading}
                      onClick={onClose}>
                      Zrušiť
                    </Button>
                    <Button
                      disabled={isOrderCreateVerifyLoading}
                      size="2xl"
                      className="grow"
                      onClick={resendCode}>
                      Podpísať objednávku{' '}
                      {isOrderCreateVerifyLoading ? (
                        <Spinner className="size-6" />
                      ) : (
                        <ArrowForwardIcon />
                      )}
                    </Button>
                  </div>
                </DialogFooter>
              </>
            )}

            {step === 'code' && (
              <>
                {hasCodeExpired && (
                  <div className="flex flex-col items-center gap-4 text-center">
                    <Text>Objednávka vypršala.</Text>
                    <Button
                      variant="outline"
                      onClick={() => {
                        Analytics.track('new-investment-step2-agreement-signed')
                        orderCreateVerifyMutation(undefined, {
                          onSuccess: () => {
                            setStep('code')
                          },
                        })
                      }}>
                      Znova odoslať SMS kód{` `}
                      {isOrderCreateVerifyLoading && (
                        <Spinner className="size-4" />
                      )}
                    </Button>
                  </div>
                )}

                {!hasCodeExpired && (
                  <TwoFaVerifyForm
                    submitButtonLabel="Podpísať objednávku"
                    formContent={
                      <div className="flex flex-col items-center gap-2 text-center">
                        <Text>
                          Na potrvrdenie objednávky prosím zadajte SMS kód,
                          <br />
                          ktorý vám bol zaslaný na číslo{' '}
                          {user.profile?.phone?.replace(
                            /\B(?=(\d{3})+(?!\d))/g,
                            '\xa0'
                          )}
                          .
                          <br />
                          <CodeExpirationCountdown countdown={countdown} />
                        </Text>
                      </div>
                    }
                    handleSubmitForm={({ code }) =>
                      orderCreateSignMutation(code, {
                        onSuccess: () => onSuccess(orderParams),
                      })
                    }
                    fieldLabel="Potvrdzovací SMS kód"
                    FooterComponent={(props) => (
                      <div
                        className="flex flex-col items-center justify-center mt-4 gap-2 w-full"
                        {...props}
                      />
                    )}
                    handleGoBack={onClose}
                    isRequesting={isOrderCreateSignLoading}
                    error={orderCreateSignError}
                  />
                )}
              </>
            )}
          </>
        )}

        {step === 'success' && <Text>Objednávka bola úspešne podpísaná</Text>}
      </DialogContent>
    </Dialog>
  )
}
